// Mastermind mentors — the roster + the pure "who should speak this week" logic.
// The advice strings themselves live in the web layer (content); here we keep the
// testable part: which trigger conditions are active given the player's standing.

export type MentorId =
  | 'richDad'
  | 'investor'
  | 'propertyQueen'
  | 'mindsetCoach'
  | 'techFounder'
  | 'socialStrategist'
  | 'banker'
  | 'philanthropist';

export type MentorTrigger =
  | 'lowSavings'
  | 'lowPersistence'
  | 'freedomLow'
  | 'dreamAchieved'
  | 'lossWeek'
  | 'cashIdle'
  | 'inflation'; // cash left idle for more than 2 in-game years (the Buffett-snowball warning)

export type MentorDef = {
  id: MentorId;
  name: string;
  specialty: string;
  icon: string; // portrait emoji
};

/** The eight mentors a player can recruit (up to 4). */
export const MENTOR_ROSTER: MentorDef[] = [
  { id: 'richDad', name: 'Rich Dad', specialty: 'Asset building', icon: '🎩' },
  { id: 'investor', name: 'The Investor', specialty: 'Stocks & compounding', icon: '📊' },
  { id: 'propertyQueen', name: 'The Property Queen', specialty: 'Real estate', icon: '👑' },
  { id: 'mindsetCoach', name: 'The Mindset Coach', specialty: 'Mindset principles', icon: '🧘' },
  { id: 'techFounder', name: 'The Tech Founder', specialty: 'Digital business', icon: '💻' },
  { id: 'socialStrategist', name: 'The Social Media Strategist', specialty: 'Audience building', icon: '📱' },
  { id: 'banker', name: 'The Banker', specialty: 'Credit, debt & leverage', icon: '🏦' },
  { id: 'philanthropist', name: 'The Philanthropist', specialty: 'Giving, legacy & impact', icon: '🕊️' },
];

/** Inputs to the weekly trigger evaluation — all the standing facts a mentor
 *  might react to. Kept as plain numbers so this stays pure and unit-testable. */
export type MentorSignalInput = {
  savingsValue: number; // value in the savings jar
  cash: number; // liquid lifecycle cash
  persistence: number; // mindset sub-stat 0..100
  freedomPct: number; // Freedom Meter %
  achievedDreams: number; // vision dreams achieved
  lastWeekCashflow: number | null; // most recent settled-week cashflow (null = none yet)
  totalAssetValue: number; // value tied up in assets
  cashIdleYears: number; // consecutive in-game years cash has sat uninvested
};

/** Mastermind unlocks at the Explorer bracket — age 11 and up. */
export function mastermindUnlocked(age: number): boolean {
  return age >= 11;
}

/**
 * Which trigger conditions are active this week. A mentor only speaks if one of
 * the triggers they cover appears here.
 */
export function computeMentorSignals(i: MentorSignalInput): MentorTrigger[] {
  const out: MentorTrigger[] = [];
  // Holding cash but barely saving any of it.
  if (i.savingsValue < 20 && i.cash >= 40) out.push('lowSavings');
  // Discipline is slipping.
  if (i.persistence < 40) out.push('lowPersistence');
  // A long way from freedom.
  if (i.freedomPct < 30) out.push('freedomLow');
  // Something on the vision board came true.
  if (i.achievedDreams > 0) out.push('dreamAchieved');
  // The business lost money last week.
  if (i.lastWeekCashflow != null && i.lastWeekCashflow < 0) out.push('lossWeek');
  // Cash piling up instead of being put to work.
  if (i.cash >= 100 && i.cash > i.totalAssetValue) out.push('cashIdle');
  // Buffett-snowball warning: cash left idle for more than two in-game years.
  if (i.cashIdleYears > 2) out.push('inflation');
  return out;
}
